import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PLAN_COVERAGE_LOW, type HourTypes } from '@pz/core';
import { h, pct } from '../lib/format';
import { StackBars } from './charts';

/**
 * Hour types and the terms of the reports (request 19 of round 4). The reports of a person use the booked hours (Ist);
 * plan hours only through the plan/actual ratio of finished tasks; billed hours (what the office puts on the customer
 * document) stay in the project and billing reports. These pieces say so where the figures are shown.
 */

const PROJECT_TYPES = ['billable', 'included', 'nonBillableProject'] as const;

/** Hours of a person by type: project work (dark) and internal work (light), as in "hours by area". */
export function HourTypeBars({ types }: { types: HourTypes }) {
  const { t } = useTranslation();
  if (!types.total) return <p className="text-sm text-graphite">{t('common.noData')}</p>;
  const rows = [
    ...PROJECT_TYPES.filter((k) => types[k] > 0).map((k) => ({ id: k, label: t(`hourTypes.${k}`), values: { project: types[k] }, end: `${h(types[k], 1)} · ${pct(types[k] / types.total)}` })),
    ...(types.internal > 0 ? [{ id: 'internal', label: t('hourTypes.internal'), values: { internal: types.internal }, end: `${h(types.internal, 1)} · ${pct(types.internal / types.total)}` }] : []),
  ];
  return (
    <StackBars
      head={t('hourTypes.type')}
      labelWidth="15rem"
      series={[
        { key: 'project', label: t('areas.project'), kind: 'strong' },
        { key: 'internal', label: t('areas.internal'), kind: 'soft' },
      ]}
      rows={rows}
    />
  );
}

/** One line per type for a tooltip (title attribute): "Abrechenbar: 12,5 h" … */
export function useHourTypesTitle() {
  const { t } = useTranslation();
  return (types: HourTypes): string =>
    [...PROJECT_TYPES, 'internal' as const]
      .filter((k) => types[k] > 0)
      .map((k) => `${t(`hourTypes.${k}`)}: ${h(types[k], 1)} h`)
      .join('\n');
}

/** The terms behind the figures: plan, booked, billed and the four hour types. Folded by default. */
export function HourTerms({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <details className={className ?? 'rounded-lg border border-line bg-sheet px-3 py-2 text-sm'}>
      <summary className="cursor-pointer font-medium select-none">
        <Info className="mr-1 inline size-4 text-graphite" aria-hidden />
        {t('hourTypes.termsTitle')}
      </summary>
      <dl className="mt-2 grid gap-x-6 gap-y-2 text-xs md:grid-cols-2">
        {(['plan', 'booked', 'billed', 'billable', 'included', 'nonBillableProject', 'internal', 'example'] as const).map((k) => (
          <div key={k}>
            <dt className="font-semibold text-ink">{t(`hourTypes.terms.${k}.name`)}</dt>
            <dd className="text-graphite">{t(`hourTypes.terms.${k}.desc`)}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

export interface CoverageView {
  coverage: number;
  unplannedMinutes: number;
  unplannedTasks: number;
}

export const coverageLow = (c: CoverageView) => c.unplannedMinutes > 0 && Number.isFinite(c.coverage) && c.coverage < PLAN_COVERAGE_LOW;

/** Text for a ratio: which share of the hours on finished tasks it covers (empty when every finished task had a plan). */
export function useCoverageText() {
  const { t } = useTranslation();
  return (c: CoverageView) =>
    c.unplannedMinutes > 0 ? t('hourTypes.coverageTitle', { pct: pct(c.coverage), hours: h(c.unplannedMinutes, 1), count: c.unplannedTasks }) : '';
}
